import { alpha } from '@mui/material/styles';

import { brand, semantic, radius, shadow, density, touch, query, motion, weight } from './tokens';

// ==============================|| COMPONENT STYLE OVERRIDES ||============================== //
//
// NOTE: this file deliberately does NOT override MuiTypography font sizes.
// The single type scale lives in `themes/typography.js` (built from
// tokens.type). A MuiTypography styleOverride wins over the variant styles and
// would silently shadow that scale - which is exactly the bug this replaced.

export default function componentStyleOverrides(theme, borderRadius, outlinedFilled) {
    const isDark = theme.palette.mode === 'dark';

    // Mode-aware neutrals. Previously these were hardcoded light-mode hexes,
    // which is why dark mode rendered unreadable labels and surfaces.
    const surface = isDark ? theme.palette.background.paper : brand.white;
    const canvas = isDark ? theme.palette.background.default : semantic.grey[50];
    const textPrimary = isDark ? theme.palette.text.primary : semantic.text.primary;
    const textSecondary = isDark ? theme.palette.text.secondary : semantic.text.secondary;
    const borderColor = isDark ? alpha(brand.white, 0.16) : semantic.grey[200];
    const primary = isDark ? brand.navy[300] : semantic.primary.main;
    const filledInputBg = isDark ? alpha(brand.white, 0.04) : canvas;

    // Honour the user-configurable radius, but keep every component on the
    // same scale rather than the 19 ad-hoc values used before.
    const r = {
        control: borderRadius ?? radius.md,
        surface: (borderRadius ?? radius.md) + 4
    };

    return {
        MuiCssBaseline: {
            styleOverrides: {
                html: {
                    // Stops a single wide table or fixed-width child from
                    // making the whole page pan sideways on a phone.
                    maxWidth: '100%',
                    overflowX: 'hidden',
                    WebkitTextSizeAdjust: '100%'
                },
                body: {
                    backgroundColor: canvas,
                    color: textPrimary,
                    maxWidth: '100%',
                    overflowX: 'hidden'
                },
                // Media never forces a horizontal scrollbar.
                'img, video, canvas, svg': {
                    maxWidth: '100%'
                },
                // Consistent, unobtrusive scrollbars across the dense tables.
                '*::-webkit-scrollbar': { width: 10, height: 10 },
                '*::-webkit-scrollbar-thumb': {
                    backgroundColor: isDark ? alpha(brand.white, 0.18) : semantic.grey[300],
                    borderRadius: radius.pill,
                    border: `2px solid transparent`,
                    backgroundClip: 'content-box'
                },
                '*::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: isDark ? alpha(brand.white, 0.28) : semantic.grey[400]
                }
            }
        },

        MuiButton: {
            defaultProps: {
                disableElevation: true
            },
            styleOverrides: {
                root: {
                    minHeight: density.controlHeight,
                    fontWeight: weight.semibold,
                    textTransform: 'none',
                    padding: `${density.buttonPaddingY}px ${density.buttonPaddingX}px`,
                    borderRadius: r.control,
                    transition: `background-color ${motion.quick}, box-shadow ${motion.quick}, border-color ${motion.quick}`,
                    '&:focus-visible': {
                        outline: 'none',
                        boxShadow: shadow.focus
                    },
                    '&.Mui-disabled': {
                        boxShadow: 'none'
                    },
                    [query.coarse]: {
                        minHeight: touch.controlHeight,
                        paddingTop: touch.buttonPaddingY,
                        paddingBottom: touch.buttonPaddingY
                    }
                },
                sizeSmall: {
                    minHeight: density.controlHeightSmall,
                    padding: '4px 12px',
                    [query.coarse]: { minHeight: 38 }
                },
                sizeLarge: {
                    minHeight: density.controlHeightLarge,
                    padding: '12px 24px'
                },
                containedPrimary: {
                    backgroundColor: semantic.primary.main,
                    color: semantic.primary.contrastText,
                    '&:hover': { backgroundColor: semantic.primary.dark }
                },
                outlined: {
                    borderColor,
                    '&:hover': { borderColor: primary, backgroundColor: alpha(primary, 0.04) }
                },
                text: {
                    '&:hover': { backgroundColor: alpha(primary, 0.06) }
                }
            }
        },

        MuiIconButton: {
            styleOverrides: {
                root: {
                    borderRadius: radius.sm,
                    transition: `background-color ${motion.quick}`,
                    '&:focus-visible': {
                        outline: 'none',
                        boxShadow: shadow.focus
                    },
                    // Row-action icons are the most-missed tap target in the
                    // table views; give them the full 44px on touch.
                    [query.coarse]: {
                        minWidth: touch.minTarget,
                        minHeight: touch.minTarget
                    }
                }
            }
        },

        MuiInputLabel: {
            styleOverrides: {
                root: {
                    color: textSecondary,
                    fontWeight: weight.medium,
                    transition: `color ${motion.quick}`,
                    '&.Mui-focused': {
                        color: primary,
                        fontWeight: weight.semibold
                    },
                    '&.Mui-error': { color: semantic.error.main }
                }
            }
        },

        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    backgroundColor: outlinedFilled ? filledInputBg : 'transparent',
                    borderRadius: r.control,
                    minHeight: density.controlHeight,
                    color: textPrimary,
                    // No backdrop-filter here. It used to blur every input on
                    // the page, which cost a compositing layer per field on
                    // 20+ field forms and muddied text against dense tables.
                    transition: `background-color ${motion.quick}, box-shadow ${motion.quick}`,
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor,
                        borderWidth: 1,
                        transition: `border-color ${motion.quick}`
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: isDark ? alpha(brand.white, 0.32) : semantic.grey[400]
                    },
                    '&.Mui-focused': {
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: primary,
                            borderWidth: 1
                        },
                        boxShadow: shadow.focus
                    },
                    '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: semantic.error.main },
                    '&.Mui-disabled': {
                        backgroundColor: isDark ? alpha(brand.white, 0.02) : semantic.grey[100]
                    },
                    [query.coarse]: { minHeight: touch.controlHeight }
                },
                input: {
                    padding: `${density.inputPaddingY}px ${density.inputPaddingX}px`,
                    '&::placeholder': {
                        color: textSecondary,
                        opacity: 0.7
                    },
                    [query.coarse]: {
                        paddingTop: touch.inputPaddingY,
                        paddingBottom: touch.inputPaddingY
                    },
                    // 16px minimum stops iOS Safari zooming the viewport on focus.
                    [query.phone]: { fontSize: touch.inputFontSize }
                },
                inputSizeSmall: {
                    padding: `6px ${density.inputPaddingX}px`,
                    [query.phone]: { fontSize: touch.inputFontSize }
                },
                inputMultiline: {
                    padding: 0
                }
            }
        },

        MuiSelect: {
            styleOverrides: {
                select: {
                    padding: `${density.inputPaddingY}px ${density.inputPaddingX}px`,
                    '&:focus': { backgroundColor: 'transparent' },
                    '&.MuiInputBase-input': {
                        minHeight: '1.4375em',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden'
                    },
                    [query.coarse]: {
                        paddingTop: touch.inputPaddingY,
                        paddingBottom: touch.inputPaddingY
                    },
                    [query.phone]: { fontSize: touch.inputFontSize }
                },
                icon: {
                    color: textSecondary,
                    transition: `color ${motion.quick}`,
                    right: 10
                }
            }
        },

        MuiMenu: {
            styleOverrides: {
                paper: {
                    marginTop: 6,
                    minWidth: 200,
                    backgroundColor: surface,
                    backgroundImage: 'none',
                    borderRadius: r.surface,
                    border: `1px solid ${borderColor}`,
                    boxShadow: shadow.md,
                    '& .MuiList-root': { padding: 6 }
                }
            }
        },

        MuiMenuItem: {
            styleOverrides: {
                root: {
                    borderRadius: radius.sm,
                    padding: `${density.menuItemPaddingY}px ${density.menuItemPaddingX}px`,
                    transition: `background-color ${motion.quick}`,
                    '&.Mui-selected': {
                        backgroundColor: alpha(primary, 0.12),
                        color: primary,
                        fontWeight: weight.semibold,
                        '&:hover': { backgroundColor: alpha(primary, 0.18) }
                    },
                    '&:hover': { backgroundColor: alpha(primary, 0.08) },
                    [query.coarse]: { minHeight: touch.minTarget }
                }
            }
        },

        MuiPaper: {
            styleOverrides: {
                root: { backgroundImage: 'none' },
                rounded: { borderRadius: r.surface }
            }
        },

        // 15 of the app's 23 table containers had no overflow rule, so a wide
        // table pushed the whole page sideways. Scrolling is now contained.
        MuiTableContainer: {
            styleOverrides: {
                root: {
                    maxWidth: '100%',
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    overscrollBehaviorX: 'contain'
                }
            }
        },

        MuiTable: {
            styleOverrides: {
                root: {
                    // Keeps column widths stable while paging through results.
                    borderCollapse: 'separate',
                    borderSpacing: 0
                }
            }
        },

        MuiTableCell: {
            styleOverrides: {
                root: {
                    padding: `${density.tableCellPaddingY}px ${density.tableCellPaddingX}px`,
                    borderBottom: `1px solid ${borderColor}`,
                    [theme.breakpoints.down('sm')]: {
                        paddingLeft: 12,
                        paddingRight: 12
                    },
                    [query.coarse]: {
                        paddingTop: touch.tableCellPaddingY,
                        paddingBottom: touch.tableCellPaddingY
                    }
                },
                head: {
                    fontWeight: weight.semibold,
                    lineHeight: 1.4,
                    backgroundColor: isDark ? brand.navy[800] : semantic.primary.main,
                    color: semantic.primary.contrastText,
                    borderBottom: 'none',
                    whiteSpace: 'nowrap'
                },
                sizeSmall: {
                    padding: `${density.tableCellPaddingDenseY}px ${density.tableCellPaddingX}px`
                }
            }
        },

        MuiTableRow: {
            styleOverrides: {
                root: {
                    transition: `background-color ${motion.quick}`,
                    '&:last-of-type td': { borderBottom: 'none' }
                },
                hover: {
                    '&:hover': { backgroundColor: alpha(primary, 0.04) }
                }
            }
        },

        MuiTabs: {
            defaultProps: {
                // Tab strips swipe instead of wrapping or clipping on narrow
                // screens. An explicit `variant` on a call site still wins.
                variant: 'scrollable',
                scrollButtons: 'auto',
                allowScrollButtonsMobile: true
            },
            styleOverrides: {
                root: { minHeight: 42 },
                indicator: { height: 2 },
                scrollButtons: {
                    '&.Mui-disabled': { opacity: 0.3 }
                }
            }
        },

        MuiTab: {
            styleOverrides: {
                root: {
                    fontWeight: weight.semibold,
                    padding: '10px 18px',
                    minHeight: 42,
                    textTransform: 'none',
                    '&:focus-visible': {
                        outline: 'none',
                        boxShadow: shadow.focus
                    },
                    [theme.breakpoints.down('sm')]: { padding: '10px 14px' },
                    [query.coarse]: { minHeight: touch.minTarget }
                }
            }
        },

        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: radius.sm,
                    height: 24,
                    fontWeight: weight.semibold
                },
                sizeSmall: { height: 20 },
                label: { padding: '0 8px' }
            }
        },

        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    marginLeft: 2,
                    marginTop: 4,
                    '&.Mui-error': { color: semantic.error.main }
                }
            }
        },

        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: r.surface,
                    backgroundImage: 'none',
                    boxShadow: shadow.lg,
                    // Only 5 of the app's 34 dialogs opt into `fullScreen`.
                    // Rather than touch 29 call sites, every dialog goes
                    // full-bleed below sm - which is what a phone wants anyway.
                    [theme.breakpoints.down('sm')]: {
                        margin: 0,
                        width: '100%',
                        maxWidth: '100%',
                        maxHeight: '100%',
                        height: '100%',
                        borderRadius: 0
                    }
                },
                // Any hardcoded `minWidth` inside a dialog cannot exceed the screen.
                paperScrollPaper: {
                    [theme.breakpoints.down('sm')]: { minWidth: 0 }
                }
            }
        },

        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    fontWeight: weight.semibold,
                    padding: '16px 20px',
                    borderBottom: `1px solid ${borderColor}`,
                    [theme.breakpoints.down('sm')]: { padding: '14px 16px' }
                }
            }
        },

        MuiDialogContent: {
            styleOverrides: {
                root: {
                    padding: 20,
                    [theme.breakpoints.down('sm')]: { padding: 16 },
                    // MUI ships `.MuiDialogTitle-root + & { paddingTop: 0 }`.
                    // That selector is (0,2,0) so it outranks any `sx` padding
                    // a call site sets. With outlined fields in the first row
                    // the shrunk label renders ~9px above the input border and
                    // gets clipped by DialogContent's own `overflow-y: auto`.
                    // Matching the specificity here restores the headroom.
                    '.MuiDialogTitle-root + &': {
                        paddingTop: 20,
                        [theme.breakpoints.down('sm')]: { paddingTop: 16 }
                    }
                }
            }
        },

        MuiDialogActions: {
            styleOverrides: {
                root: {
                    padding: '12px 20px',
                    borderTop: `1px solid ${borderColor}`,
                    gap: 8,
                    // Stacked full-width buttons beat three cramped ones in a row.
                    [theme.breakpoints.down('sm')]: {
                        padding: '12px 16px',
                        flexDirection: 'column-reverse',
                        alignItems: 'stretch',
                        '& > :not(style) ~ :not(style)': { marginLeft: 0 }
                    }
                }
            }
        },

        MuiAlert: {
            styleOverrides: {
                root: { borderRadius: r.control },
                message: { fontWeight: weight.medium },
                standardSuccess: {
                    backgroundColor: alpha(semantic.success.main, 0.1),
                    color: isDark ? semantic.success.light : semantic.success.dark
                },
                standardError: {
                    backgroundColor: alpha(semantic.error.main, 0.1),
                    color: isDark ? semantic.error.light : semantic.error.dark
                },
                standardWarning: {
                    backgroundColor: alpha(semantic.warning.main, 0.12),
                    color: isDark ? semantic.warning.light : semantic.warning.dark
                },
                standardInfo: {
                    backgroundColor: alpha(primary, 0.1),
                    color: isDark ? brand.navy[200] : semantic.primary.main
                }
            }
        },

        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    padding: '6px 10px',
                    borderRadius: radius.sm,
                    backgroundColor: isDark ? semantic.grey[700] : semantic.grey[800]
                },
                arrow: {
                    color: isDark ? semantic.grey[700] : semantic.grey[800]
                }
            }
        },

        MuiListSubheader: {
            styleOverrides: {
                root: {
                    fontWeight: weight.semibold,
                    color: textSecondary,
                    backgroundColor: 'transparent',
                    padding: '10px 16px',
                    lineHeight: 1.5,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                }
            }
        },

        MuiCheckbox: {
            styleOverrides: {
                root: {
                    padding: 6,
                    '& .MuiSvgIcon-root': { fontSize: 20 },
                    [query.coarse]: { padding: touch.togglePadding }
                }
            }
        },

        MuiRadio: {
            styleOverrides: {
                root: {
                    padding: 6,
                    '& .MuiSvgIcon-root': { fontSize: 20 },
                    [query.coarse]: { padding: touch.togglePadding }
                }
            }
        },

        MuiSwitch: {
            styleOverrides: {
                root: {
                    [query.coarse]: { padding: touch.togglePadding }
                }
            }
        },

        // Row-per-page controls and page arrows are unusable at desktop size
        // on a phone; let the bar wrap instead of overflowing.
        MuiTablePagination: {
            styleOverrides: {
                toolbar: {
                    [theme.breakpoints.down('sm')]: {
                        paddingLeft: 8,
                        flexWrap: 'wrap',
                        rowGap: 4
                    }
                },
                selectLabel: {
                    [theme.breakpoints.down('sm')]: { display: 'none' }
                },
                displayedRows: {
                    margin: 0
                }
            }
        },

        MuiDivider: {
            styleOverrides: {
                root: { borderColor }
            }
        },

        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: r.surface,
                    border: `1px solid ${borderColor}`,
                    boxShadow: shadow.sm
                }
            }
        }
    };
}
